import { useCallback, useRef, useState } from 'react'
import { reportGlobalError } from '@/utils/GlobalErrorReporter'

export interface ConfirmationConfig<T> {
	title: string
	description: string | ((item: T) => string)
	onConfirm: (item: T) => Promise<void>
	confirmText?: string
	cancelText?: string
	variant?: 'default' | 'destructive'
	// Optional secondary action button
	actionText?: string
	onAction?: (item: T) => Promise<void>
	actionVariant?: 'default' | 'destructive'
}

/**
 * Generic hook for managing confirmation dialogs
 * Replaces repetitive confirmation state and handlers
 *
 * Uses ref pattern for item state to keep callbacks stable and avoid
 * stale closures. Config is assumed stable (defined at mount time).
 *
 * @example
 * const deleteConfirm = useConfirmation({
 *   title: 'Delete Item',
 *   description: (item) => `Delete "${item.name}"?`,
 *   onConfirm: async (item) => await deleteItem(item.id),
 *   variant: 'destructive',
 * })
 *
 * // Usage:
 * deleteConfirm.prompt({ id: 1, name: 'Test' })
 */
export function useConfirmation<T>(config: ConfirmationConfig<T>) {
	const [item, setItem] = useState<T | null>(null)
	const [isConfirming, setIsConfirming] = useState(false)

	// Ref pattern: keep callbacks stable by reading current item from ref
	const itemRef = useRef<T | null>(item)
	itemRef.current = item

	const configRef = useRef(config)
	configRef.current = config

	const prompt = useCallback((itemToConfirm: T) => {
		setItem(itemToConfirm)
	}, [])

	const confirm = useCallback(async () => {
		const currentItem = itemRef.current
		if (!currentItem) return

		setIsConfirming(true)
		try {
			await configRef.current.onConfirm(currentItem)
			setItem(null)
		} catch (error) {
			// Report error to ErrorReportDialog instead of console.error
			// to avoid triggering Bun's dev error overlay
			const errorObj = error instanceof Error ? error : new Error(String(error))
			reportGlobalError(errorObj)
			throw error
		} finally {
			setIsConfirming(false)
		}
	}, [])

	const cancel = useCallback(() => {
		setItem(null)
	}, [])

	const action = useCallback(async () => {
		const currentItem = itemRef.current
		const onAction = configRef.current.onAction
		if (!currentItem || !onAction) return

		setIsConfirming(true)
		try {
			await onAction(currentItem)
			setItem(null)
		} catch (error) {
			const errorObj = error instanceof Error ? error : new Error(String(error))
			reportGlobalError(errorObj)
			throw error
		} finally {
			setIsConfirming(false)
		}
	}, [])

	const getDescription = useCallback(() => {
		const currentItem = itemRef.current
		if (!currentItem) return ''
		const description = configRef.current.description
		return typeof description === 'function'
			? description(currentItem)
			: description
	}, [])

	return {
		item,
		isOpen: item !== null,
		isConfirming,
		prompt,
		confirm,
		cancel,
		action,
		getDescription,
		config,
	}
}
